import { useEffect, useRef, useState } from 'react'
import { SBack, SMessage, SSteps } from 'shj-components'
import { Button, message, Space, Spin } from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import FirstContent from './steps/firstContent'
import SecondContent from './steps/secondContent'
import ThirdContent from './steps/ThirdContent'
import FourthContent from './steps/FourthContent'
import styles from './index.module.less'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  getGoodsManagementInfo,
  goodsManagementCleanCache,
  goodsManagementGoodsQuotaConfNextSave,
  goodsManagementGoodsSaleConfSave,
  goodsManagementSaveEditInfo,
  goodsManagementSubmit,
} from '@/services/goods.ts'

export default function CommodityEdit(props) {
  const navigate = useNavigate()
  const { id } = useParams()
  const [search] = useSearchParams()
  const copyId = search.get('id')
  const [current, setCurrent] = useState(0)
  const [stepItems, setStepItems] = useState<any>([])
  const [hasSave, setHasSave] = useState(false)
  const [loading, setLoading] = useState(false)
  const [infoLoading, setInfoLoading] = useState(false)
  const [cacheId, setCacheId] = useState(id)
  const [isEdit, setIsEdit] = useState(false);
  const [dealWay, setDealWay] = useState('DIRECT') // DIRECT 直接付款  SING_PAY 签约付款
  const [bizTagCode, setBizTagCode] = useState('') // productTypeRetail  不可选择签约付款
  const [detail, setDetail] = useState({})
  const childRef1: any = useRef(null)
  const childRef2: any = useRef(null)
  const childRef3: any = useRef(null)
  const childRef4: any = useRef(null)
  const saveApi = [
    goodsManagementSaveEditInfo,
    goodsManagementGoodsSaleConfSave,
    goodsManagementGoodsQuotaConfNextSave,
  ]

  useEffect(() => {
    if (copyId || cacheId) {
      setIsEdit(true);
      getInfo();
    }
  }, [])

  const getInfo = () => {
    setInfoLoading(true)
    getGoodsManagementInfo({ id: copyId || cacheId })
      .then(res => {
        setDetail(res)
      })
      .finally(() => setInfoLoading(false))
  }

  const next = (goNext = true) => {
    return new Promise((resolve, reject) => {
      const refList = [childRef1, childRef2, childRef3, childRef4]
      const currentForm = refList?.[current]?.current
      if (current === 2) {
        if (currentForm?.getFormData?.templateListLength === 0) {
          message.error('请选择含有模版配置的签约模板')
          return
        }
        if (currentForm?.getFormData?.chooseTemplate) {
          setLoading(true)
          saveApi[current]({
            data: { id: cacheId || copyId, signTemplateId: currentForm?.getFormData?.chooseTemplate },
          })
            .then(() => {
              setCurrent(current + 1)
            })
            .finally(() => setLoading(false))
          return
        } else {
          message.error('请选择签约模板')
          return
        }
      }
      currentForm?.form?.validateFields().then(() => {
        const { fileInfos } = currentForm?.getFormData
        if (current === 0 && !fileInfos.length) return message.error('商品详情不能为空')
        setLoading(true)
        saveApi[current](currentForm?.getFormData)
          .then((res: any) => {
            if (current === 0) {
              setCacheId(res.id)
              setDetail({...detail, ...res})
            }
            goNext && setCurrent(current + 1)
            resolve()
          })
          .catch(reject)
          .finally(() => setLoading(false))
      })
    })
  }

  const prev = () => {
    setCurrent(current - 1)
  }

  const save = async () => {
    if (current <= 1) {
      await next(false)
    }
    goodsManagementSubmit(cacheId).then(res => {
      message.success(res.message || '操作成功')
      setHasSave(true)
    })
  }

  const onArriveGoods = () => {
    navigate(
      '/config/commodityManage/commodityArrive/' + cacheId + `?saleCatId=${detail.saleCatId}`
    )
  }

  const steps = [
    {
      title: '基础信息',
      content: (
        <FirstContent
          ref={childRef1}
          detail={detail}
          copyId={copyId}
          setBizTagCode={setBizTagCode}
        />
      ),
    },
    {
      title: '销售配置',
      content: (
        <SecondContent
          goodsId={cacheId}
          detail={detail}
          copyId={copyId}
          isEdit={isEdit}
          ref={childRef2}
          bizTagCode={bizTagCode}
          setDealWay={setDealWay}
        />
      ),
    },
    {
      title: '合同配置',
      dynamic: true,
      content: (
        <ThirdContent
          detail={detail}
          goodsId={cacheId}
          copyId={copyId}
          current={current}
          ref={childRef3}
        />
      ),
    },
    {
      title: '收款规则',
      dynamic: true,
      content: <FourthContent goodsId={cacheId} copyId={copyId} ref={childRef4} />,
    },
  ]

  const goBack = () => {
    SMessage.confirm({
      title: '确认继续返回',
      content: '返回将不会保存已添加信息',
      onOk: () => {
        if (cacheId) {
          return goodsManagementCleanCache({ cacheId }).then(res => {
            message.success(res.message || '操作成功')
            history.back()
          })
        } else {
          history.back()
        }
      },
    })
  }

  useEffect(() => {
    let items: any = []
    if (dealWay === 'DIRECT') {
      items = steps
        .filter(item => !item.dynamic)
        .map(item => ({ key: item.title, title: item.title }))
    } else {
      items = steps.map(item => ({ key: item.title, title: item.title }))
    }
    setStepItems(items)
  }, [dealWay])

  return (
    <Spin spinning={infoLoading}>
      <SBack
        title={`${id ? '编辑' : '新增'}商品`}
        level={2}
        extra={
          !hasSave ? (
            <Space >
              <Button loading={loading} onClick={() => goBack()}>
                取消
              </Button>
              {current > 0 && (
                <Button loading={loading} onClick={() => prev()}>
                  上一步
                </Button>
              )}
              {current !== stepItems.length - 1 ? (
                <Button loading={loading} type="primary" onClick={() => next()}>
                  下一步
                </Button>
              ) : (
                <Button loading={loading} type="primary" onClick={() => save()}>
                  保存
                </Button>
              )}
            </Space>
          ) : null
        }
      >
        <div className={styles.detailsMain}>
          {!hasSave ? (
            <>
              <div className={styles.detailsContent}>
                <div className={styles.step_wrap}>
                  <SSteps
                    current={current}
                    items={stepItems}
                    // size="small"
                    style={{
                      width: stepItems.length === 2 ? 400 : 800,
                      margin: '0 auto',
                    }}
                  />
                </div>
                {steps.map((item, index) => (
                  <div style={{ display: current === index ? 'block' : 'none' }}>
                    {item?.content}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.finishContent}>
              <CheckCircleFilled className={styles.successIcon} />
              <div className={styles.successFont}>商品{id ? '编辑' : '创建'}成功</div>
              <div className={styles.buttonBox}>
                <Button onClick={() => history.back()}>返回</Button>
                <Button
                  type="primary"
                  className={styles.buttonFont}
                  onClick={() => onArriveGoods()}
                >
                  去上架商品
                </Button>
              </div>
            </div>
          )}
        </div>
      </SBack>
    </Spin>
  )

  // <micro-app name="dataview" url="http://localhost:3100/"></micro-app>
}
